import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { RTVIClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

const LiveInterviewSessionPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Interview state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [interviewEnded, setInterviewEnded] = useState(false);
  
  // Proctoring state - enhanced with backend API
  const [faceDetected, setFaceDetected] = useState(true);
  const [faceDetectionStatus, setFaceDetectionStatus] = useState('checking'); // checking, ok, no_face, multiple_faces
  const [faceCount, setFaceCount] = useState(0);
  const [eyesVisible, setEyesVisible] = useState(true);
  const [deviceDetected, setDeviceDetected] = useState(false);
  const [cursorOutOfBounds, setCursorOutOfBounds] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [warningCount, setWarningCount] = useState(0);
  const [warningBanner, setWarningBanner] = useState({ show: false, message: '' });
  const [lastWarningTime, setLastWarningTime] = useState(0);
  const [gracePeriodActive, setGracePeriodActive] = useState(true);
  const [eyesNotVisibleSince, setEyesNotVisibleSince] = useState(null);

  // Refs
  const rtviClientRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const faceDetectionRef = useRef(null);
  const cursorTrackingRef = useRef(null);
  const proctoringIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const eyesTimerRef = useRef(null);
  const transcriptRef = useRef([]); // Keep track of transcript for disconnect handler
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);
  // Audio-only recording refs (mixed bot + candidate for Gemini evaluation)
  const audioRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const [audioEvaluating, setAudioEvaluating] = useState(false);
  
  // Interview config from location state
  const interviewConfig = location.state || {
    interviewType: 'interviewer',
    language: 'en-US',
    duration: 5,
    jobTitle: 'Position',
    candidateName: 'Candidate'
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

  // Initialize timer on mount
  useEffect(() => {
    setTimeRemaining(interviewConfig.duration * 60);
  }, [interviewConfig.duration]);

  // Initialize interview
  useEffect(() => {
    initializeInterview();
    startProctoring();
    
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeInterview = async () => {
    try {
      // Setup camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: false // Audio handled by RTVI
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Only setup recording stream for real job interviews (not trainer sessions)
      const isRealInterview = interviewConfig.interviewType === 'interviewer';
      if (isRealInterview) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const recordingStream = new MediaStream([
            ...stream.getVideoTracks(),
            ...audioStream.getAudioTracks()
          ]);
          recordingStreamRef.current = recordingStream;
          console.log('Recording stream ready for job interview (video + audio)');
        } catch (audioErr) {
          console.warn('Could not get audio for recording, using video only:', audioErr);
          recordingStreamRef.current = stream;
        }
      } else {
        console.log('Trainer session — video recording skipped');
      }

      // Setup RTVI client
      await setupRTVIClient();

    } catch (error) {
      console.error('Error initializing interview:', error);
      issueWarning('Failed to initialize camera or microphone');
    }
  };

  const setupRTVIClient = async () => {
    try {
      let baseUrl = process.env.REACT_APP_API_URL || 'https://aptirix-backend-469811621499.asia-south1.run.app';
      
      // Remove /api suffix if present for cleaner URL handling
      baseUrl = baseUrl.replace('/api', '');
      
      const params = new URLSearchParams({
        application_id: applicationId,
        interview_type: interviewConfig.interviewType,
        language: interviewConfig.language,
        duration_minutes: interviewConfig.duration.toString(),
        candidate_name: interviewConfig.candidateName,
        job_title: interviewConfig.jobTitle,
        company_name: interviewConfig.company || 'Company',
        candidate_email: interviewConfig.candidateEmail || '',
        candidate_id: interviewConfig.candidateId || '',
        voice: interviewConfig.voice || 'female-1',
      });

      const transport = new WebSocketTransport();
      
      const rtviConfig = {
        transport,
        params: {
          baseUrl: baseUrl,  // Use HTTP/HTTPS URL - RTVI will handle the protocol conversion
          endpoints: {
            connect: `/api/live-interview/connect?${params.toString()}`,
          },
        },
        enableMic: true,
        enableCam: false,
        callbacks: {
          onConnected: () => {
            setIsConnected(true);
            setConnectionStatus('Connected');
            console.log('Connected to live interview');
          },
          onDisconnected: () => {
            console.log('🔌 onDisconnected callback triggered');
            console.log('🔌 Disconnect stack trace:', new Error().stack);
            console.log('🔌 Interview ended flag:', interviewEnded);
            console.log('🔌 Current transcript length:', transcriptRef.current?.length || 0);
            
            setIsConnected(false);
            setIsMicEnabled(false);
            setIsListening(false);
            setIsSpeaking(false);
            
            // Check if this is a normal completion or an error
            // Use ref to get current transcript value
            const currentTranscript = transcriptRef.current;
            if (currentTranscript && currentTranscript.length > 0) {
              console.log('✅ Interview session completed normally');
              setConnectionStatus('Interview Complete');
            } else {
              console.log('⚠️ Disconnected unexpectedly');
              setConnectionStatus('Disconnected');
            }
          },
          onBotReady: async (data) => {
            console.log('Bot ready:', data);
            const tracks = rtviClientRef.current?.tracks();
            console.log('🎙️ Bot tracks available:', {
              hasBot: !!tracks?.bot,
              hasBotAudio: !!tracks?.bot?.audio,
              botAudioReadyState: tracks?.bot?.audio?.readyState,
              botAudioEnabled: tracks?.bot?.audio?.enabled,
            });

            if (tracks?.bot?.audio && audioRef.current) {
              const botTrack = tracks.bot.audio;
              // Play bot audio through speaker
              audioRef.current.srcObject = new MediaStream([botTrack]);

              // Setup mixed audio recording (bot + candidate) for Gemini evaluation
              try {
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioContextRef.current = audioCtx;

                // Resume AudioContext if suspended (Chrome requires user gesture)
                if (audioCtx.state === 'suspended') {
                  await audioCtx.resume();
                  console.log('🎙️ AudioContext resumed from suspended state');
                }
                console.log('🎙️ AudioContext state:', audioCtx.state);

                const destination = audioCtx.createMediaStreamDestination();

                // 1. Candidate's mic audio — reuse existing recording stream if available
                let userAudioTrack = null;
                if (recordingStreamRef.current) {
                  const audioTracks = recordingStreamRef.current.getAudioTracks();
                  if (audioTracks.length > 0) {
                    userAudioTrack = audioTracks[0];
                    console.log('🎙️ Reusing existing mic track for audio mix');
                  }
                }
                if (!userAudioTrack) {
                  const userAudioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                  userAudioTrack = userAudioStream.getAudioTracks()[0];
                  console.log('🎙️ Got new mic track for audio mix');
                }
                const userSource = audioCtx.createMediaStreamSource(new MediaStream([userAudioTrack]));
                userSource.connect(destination);
                console.log('🎙️ Candidate mic connected to mixer');

                // 2. Bot's audio track from RTVI
                const botStream = new MediaStream([botTrack]);
                const botSource = audioCtx.createMediaStreamSource(botStream);
                botSource.connect(destination);
                console.log('🎙️ Bot audio connected to mixer');

                // 3. Start audio-only MediaRecorder on the mixed stream
                const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                  ? 'audio/webm;codecs=opus'
                  : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
                const recorderOptions = mimeType ? { mimeType } : {};
                audioRecorderRef.current = new MediaRecorder(destination.stream, recorderOptions);
                audioChunksRef.current = [];
                audioRecorderRef.current.ondataavailable = (e) => {
                  if (e.data && e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                  }
                };
                audioRecorderRef.current.onerror = (err) => {
                  console.error('🎙️ Audio MediaRecorder error:', err);
                };
                audioRecorderRef.current.start(1000);
                console.log('🎙️ Mixed audio recording started (bot + candidate). MimeType:', mimeType || 'default');
              } catch (audioMixErr) {
                console.error('⚠️ Could not setup mixed audio recording:', audioMixErr);
                console.error('⚠️ Error details:', audioMixErr.name, audioMixErr.message);
              }
            } else {
              console.warn('🎙️ Bot audio track not available for mixing!', {
                hasTracks: !!tracks,
                hasBot: !!tracks?.bot,
                hasBotAudio: !!tracks?.bot?.audio,
                hasAudioRef: !!audioRef.current
              });
            }
          },
          onGenericMessage: (message) => {
            console.log('📨 Received message:', message);
            
            // Handle different message types
            if (message.type === 'transcription' || message.label === 'transcription') {
              const participant = message.participant || message.role || 'unknown';
              const text = message.text || message.content || '';
              
              if (!text) {
                console.log('⚠️ Empty transcription text, skipping');
                return;
              }
              
              const speaker = participant === 'bot' ? 'AI' : 'User';
              console.log(`💬 ${speaker}: ${text}`);
              
              // Check if AI is saying goodbye
              const lowerText = text.toLowerCase();
              const goodbyePhrases = [
                'goodbye',
                'good bye',
                'bye',
                'thank you for your time',
                'best of luck',
                'we will be in touch',
                'end of interview',
                'that concludes',
                'we are done',
                'interview is complete'
              ];
              
              const isGoodbye = goodbyePhrases.some(phrase => lowerText.includes(phrase));
              
              setTranscript(prev => {
                const newTranscript = [...prev, {
                  speaker: speaker,
                  text: text,
                  timestamp: new Date().toISOString()
                }];
                console.log('📝 Transcript updated, total messages:', newTranscript.length);
                transcriptRef.current = newTranscript; // Update ref for disconnect handler
                return newTranscript;
              });
              
              // Update speaking states
              if (participant === 'bot') {
                setIsSpeaking(true);
                setIsListening(false);
                
                // Auto-end interview if AI says goodbye
                if (isGoodbye) {
                  console.log('AI said goodbye, ending interview in 3 seconds...');
                  setTimeout(() => {
                    endInterview();
                  }, 3000);
                }
              } else {
                setIsSpeaking(false);
                setIsListening(true);
              }
            }
          },
          onError: (error) => {
            console.error('RTVI Error:', error);
            setConnectionStatus('Error: ' + (error.message || 'Unknown error'));
            issueWarning('Connection error occurred');

            // Log detailed error information
            console.error('Error details:', {
              message: error.message,
              code: error.code,
              type: error.type,
              stack: error.stack
            });
          },
          onTransportStateChanged: (state) => {
            console.log('🔄 Transport state changed:', state);
            
            // Handle different transport states
            if (state === 'disconnected') {
              // Check if this is a normal completion using ref
              const currentTranscript = transcriptRef.current;
              if (currentTranscript && currentTranscript.length > 0) {
                setConnectionStatus('Interview Complete');
              } else {
                setConnectionStatus('Disconnected');
              }
            } else if (state === 'error') {
              setConnectionStatus('Connection Error');
            } else {
              setConnectionStatus(state);
            }
          },
        },
      };

      const client = new RTVIClient(rtviConfig);
      rtviClientRef.current = client;
      
      // Add WebSocket-specific error handlers
      client.on('error', (error) => {
        console.error('❌ RTVI Client Error:', error);
        
        // Check if this is a 1006 closure (which might be normal session end)
        if (error.message && error.message.includes('1006')) {
          console.log('WebSocket closed with code 1006');
          
          // If we have transcript, this is likely a normal completion
          if (transcript.length > 0) {
            console.log('✅ Session completed normally (has transcript)');
            setConnectionStatus('Interview Complete');
          } else {
            console.error('⚠️ WebSocket closed abnormally (1006) - possible causes:');
            console.error('- Server crashed or restarted');
            console.error('- Network connectivity issue');
            console.error('- Firewall blocking WebSocket');
            console.error('- SSL/TLS certificate issue');
            issueWarning('Connection lost unexpectedly');
            setConnectionStatus('Connection Lost');
          }
        } else {
          issueWarning('Connection error occurred');
          setConnectionStatus('Error');
        }
      });
      
      client.on('disconnect', (reason) => {
        console.log('🔌 Disconnected:', reason);
        if (reason && reason.code === 1006) {
          console.error('Abnormal WebSocket closure detected');
        }
      });
      
      // Add additional event listeners for better transcription capture
      client.on('messageReceived', (message) => {
        console.log('🔔 Message received event:', message);
      });
      
      client.on('transcriptReceived', (transcript) => {
        console.log('📝 Transcript received event:', transcript);
      });
      
      client.on('botTranscript', (text) => {
        console.log('🤖 Bot transcript:', text);
        setTranscript(prev => [...prev, {
          speaker: 'AI',
          text: text,
          timestamp: new Date().toISOString()
        }]);
        setIsSpeaking(true);
        setIsListening(false);
      });
      
      client.on('userTranscript', (text) => {
        console.log('👤 User transcript:', text);
        setTranscript(prev => [...prev, {
          speaker: 'User',
          text: text,
          timestamp: new Date().toISOString()
        }]);
        setIsSpeaking(false);
        setIsListening(true);
      });
      
    } catch (error) {
      console.error('Error setting up RTVI client:', error);
      issueWarning('Failed to setup interview connection');
    }
  };

  const startInterview = async () => {
    try {
      if (rtviClientRef.current) {
        // Set timer BEFORE connecting
        const durationInSeconds = interviewConfig.duration * 60;
        setTimeRemaining(durationInSeconds);
        setInterviewStarted(true);
        startTimer();

        // Start video recording
        if (recordingStreamRef.current) {
          startRecording(recordingStreamRef.current);
        }

        // Then connect
        await rtviClientRef.current.connect();
      }
    } catch (error) {
      console.error('Error starting interview:', error);
      issueWarning('Failed to start interview');
    }
  };

  const endInterview = async () => {
    // Prevent multiple calls
    if (interviewEnded) {
      console.log('Interview already ended, skipping...');
      return;
    }
    
    try {
      console.log('🛑 endInterview() called - Stack trace:', new Error().stack);
      setInterviewEnded(true);
      stopTimer();

      // Stop recording and upload video to GCS (async, non-blocking)
      stopRecordingAndUpload().then(url => {
        if (url) console.log('Recording saved to GCS successfully');
        else console.log('No recording to save or upload failed');
      });

      // Stop mixed audio recorder and upload for Gemini audio evaluation
      // AWAIT this so audio evaluation completes before text-based fallback runs
      const audioEvalSuccess = await stopAudioRecordingAndEvaluate();
      console.log(`🎙️ Audio evaluation ${audioEvalSuccess ? 'succeeded' : 'skipped/failed'}`);

      if (rtviClientRef.current) {
        try {
          // Check if client is still connected before trying to disconnect
          const state = rtviClientRef.current.state;
          if (state === 'ready' || state === 'connected') {
            await rtviClientRef.current.disconnect();
          } else {
            console.log('Client already disconnected, state:', state);
          }
        } catch (disconnectError) {
          // Ignore "Session ended" errors - this is expected if backend already closed
          if (disconnectError.message && disconnectError.message.includes('Session ended')) {
            console.log('Session already ended by backend (normal)');
          } else {
            console.error('Error disconnecting client:', disconnectError);
          }
        }
      }
      
      // Save interview data and navigate to results
      const saveResult = await saveInterviewData();
      
      // Check if this is a placement interview
      const interviewType = location.state?.type;
      const isPlacementInterview = interviewType === 'placement_tech' || interviewType === 'placement_hr';
      
      setTimeout(() => {
        if (isPlacementInterview) {
          const assessmentId = location.state?.assessmentId;
          const nextRound = saveResult?.next_round;
          
          // Navigate based on next_round from backend
          if (nextRound === 'hr_interview') {
            navigate(`/placement-training/assessment/${assessmentId}/hr-interview`);
          } else if (nextRound === 'completed' || !nextRound) {
            navigate(`/placement-training/assessment/${assessmentId}/results`, {
              state: {
                fromInterview: true,
                interviewType: interviewType
              }
            });
          }
        } else if (interviewConfig.interviewType === 'trainer') {
          // Trainer session — navigate to candidate reports page
          navigate('/candidate/reports', {
            state: {
              fromTraining: true,
              sessionId: applicationId
            }
          });
        } else {
          // Real job interview — navigate to interview complete page
          navigate('/live-interview-complete', {
            state: {
              applicationId: applicationId,
              interviewType: 'live',
              transcript: transcript,
              warnings: warnings,
              duration: interviewConfig.duration
            }
          });
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error ending interview:', error);
      // Still navigate even if there's an error
      const interviewType = location.state?.type;
      const isPlacementInterview = interviewType === 'placement_tech' || interviewType === 'placement_hr';
      
      setTimeout(() => {
        if (isPlacementInterview) {
          const assessmentId = location.state?.assessmentId;
          navigate(`/placement-training/assessment/${assessmentId}/results`);
        } else if (interviewConfig.interviewType === 'trainer') {
          navigate('/candidate/reports');
        } else {
          navigate('/live-interview-complete', {
            state: {
              applicationId: applicationId,
              interviewType: 'live',
              transcript: transcript,
              warnings: warnings,
              duration: interviewConfig.duration
            }
          });
        }
      }, 2000);
    }
  };

  const toggleMicrophone = async () => {
    try {
      if (rtviClientRef.current) {
        if (isMicEnabled) {
          await rtviClientRef.current.enableMic(false);
          setIsMicEnabled(false);
        } else {
          await rtviClientRef.current.enableMic(true);
          setIsMicEnabled(true);
        }
      }
    } catch (error) {
      console.error('Error toggling microphone:', error);
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          console.log('⏰ Timer reached 0, ending interview...');
          console.log('⏰ Timer end - Stack trace:', new Error().stack);
          endInterview();
          return 0;
        }
        
        // Warning at 1 minute remaining
        if (prev === 60) {
          issueWarning('1 minute remaining');
        }
        
        // Warning at 30 seconds remaining
        if (prev === 30) {
          issueWarning('30 seconds remaining');
        }
        
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startProctoring = () => {
    console.log('Starting enhanced proctoring (YOLO + MediaPipe backend)...');

    // Grace period: 3 seconds before issuing warnings
    setGracePeriodActive(true);
    setTimeout(() => {
      setGracePeriodActive(false);
      console.log('Grace period ended, warnings now active');
    }, 3000);

    // Analyze frame every 2.5 seconds using backend YOLO + MediaPipe
    proctoringIntervalRef.current = setInterval(() => {
      analyzeFrame();
    }, 2500);

    // Eye tracking: check every second if eyes have been not visible for 3+ seconds
    eyesTimerRef.current = setInterval(() => {
      setEyesNotVisibleSince(prev => {
        if (prev) {
          const elapsed = Date.now() - prev;
          if (elapsed >= 3000) {
            issueWarning('Eyes not visible for 3 seconds. Please look at the camera.');
            logProctoringEvent('eyes_not_visible', 'warning', {
              message: 'Eyes not visible for 3+ seconds',
              duration_ms: elapsed
            });
            return null; // Reset after warning
          }
        }
        return prev;
      });
    }, 1000);

    // Cursor tracking
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const isOutside = (
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom
        );
        if (isOutside && interviewStarted && !interviewEnded) {
          setCursorOutOfBounds(true);
        } else {
          setCursorOutOfBounds(false);
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    cursorTrackingRef.current = () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  };

  const stopProctoring = () => {
    if (proctoringIntervalRef.current) {
      clearInterval(proctoringIntervalRef.current);
      proctoringIntervalRef.current = null;
    }
    if (eyesTimerRef.current) {
      clearInterval(eyesTimerRef.current);
      eyesTimerRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  };

  // ---- Video Recording (continuous for entire live interview) ----
  const startRecording = (stream) => {
    try {
      const options = {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 500000,   // 500 Kbps — compressed
        audioBitsPerSecond: 64000,    // 64 Kbps audio
      };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn('MediaRecorder: video/webm not supported, trying default');
        delete options.mimeType;
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onerror = (err) => {
        console.error('MediaRecorder error:', err);
      };

      mediaRecorderRef.current.start(1000); // Collect data every 1 second
      console.log('Recording started for live interview');
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  const stopAudioRecordingAndEvaluate = () => {
    return new Promise((resolve) => {
      if (!audioRecorderRef.current || audioRecorderRef.current.state === 'inactive') {
        console.log('No active audio recording to stop');
        resolve(false);
        return;
      }

      audioRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const sizeMB = (audioBlob.size / 1024 / 1024).toFixed(2);
        console.log(`🎙️ Mixed audio recording stopped. Size: ${sizeMB} MB, Chunks: ${audioChunksRef.current.length}`);

        if (audioBlob.size < 1000) {
          console.warn('Audio recording too small, skipping evaluation');
          resolve(false);
          return;
        }

        // Upload audio for Gemini evaluation
        setAudioEvaluating(true);
        const formData = new FormData();
        formData.append('audio', audioBlob, `interview-audio-${applicationId}.webm`);
        formData.append('session_id', applicationId);

        try {
          console.log(`🎙️ Uploading ${sizeMB} MB audio for Gemini evaluation...`);
          const response = await fetch(
            `${API_BASE_URL}/live-interview/upload-audio-for-evaluation`,
            { method: 'POST', body: formData }
          );

          if (response.ok) {
            const data = await response.json();
            console.log('✅ Audio evaluation completed:', data.overall_score);
            resolve(true);
          } else {
            const errText = await response.text();
            console.error('❌ Audio evaluation failed:', response.status, errText);
            resolve(false);
          }
        } catch (err) {
          console.error('❌ Error uploading audio for evaluation:', err);
          resolve(false);
        } finally {
          setAudioEvaluating(false);
        }

        audioChunksRef.current = [];
      };

      audioRecorderRef.current.stop();

      // Close AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    });
  };

  const stopRecordingAndUpload = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        console.log('No active recording to stop');
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
        console.log(`Recording stopped. Size: ${sizeMB} MB, Chunks: ${recordedChunksRef.current.length}`);

        if (blob.size < 1000) {
          console.warn('Recording too small, skipping upload');
          resolve(null);
          return;
        }

        // Upload to backend
        const formData = new FormData();
        formData.append('video', blob, `live-interview-${applicationId}.webm`);
        formData.append('session_id', applicationId);

        try {
          console.log(`Uploading ${sizeMB} MB recording to GCS...`);
          const response = await fetch(
            `${API_BASE_URL}/live-interview/upload-recording`,
            { method: 'POST', body: formData }
          );

          if (response.ok) {
            const data = await response.json();
            console.log('Live interview recording uploaded successfully:', data.recording_url);
            resolve(data.recording_url);
          } else {
            const errText = await response.text();
            console.error('Failed to upload recording:', response.status, errText);
            resolve(null);
          }
        } catch (err) {
          console.error('Error uploading recording:', err);
          resolve(null);
        }

        recordedChunksRef.current = [];
      };

      mediaRecorderRef.current.stop();
    });
  };

  const analyzeFrame = async () => {
    if (!videoRef.current || !interviewStarted || interviewEnded) return;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      if (canvas.width === 0 || canvas.height === 0) return;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const frameData = canvas.toDataURL('image/jpeg', 0.7);

      const response = await fetch(`${API_BASE_URL}/interview/analyze-frame`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_id: applicationId,
          frame_data: frameData
        })
      });

      if (!response.ok) return;

      const data = await response.json();
      const analysis = data.analysis;
      if (!analysis || !analysis.success) return;

      // Update state from backend analysis
      const detectedFaces = analysis.face_count || 0;
      setFaceCount(detectedFaces);
      setFaceDetected(detectedFaces >= 1);
      setDeviceDetected(analysis.device_detected || false);

      // Eye tracking: update eyes visibility and continuous timer
      const backendEyesVisible = analysis.eyes_visible !== false;
      setEyesVisible(backendEyesVisible);
      if (!backendEyesVisible) {
        setEyesNotVisibleSince(prev => prev || Date.now()); // Start timer if not already started
      } else {
        setEyesNotVisibleSince(null); // Reset timer
      }

      // Process warnings from backend
      if (analysis.warnings && analysis.warnings.length > 0) {
        for (const warning of analysis.warnings) {
          let message = '';
          let isImmediate = false;
          let severity = 'warning';

          switch (warning) {
            case 'no_face':
              message = 'No face detected. Please show your face clearly.';
              setFaceDetectionStatus('no_face');
              isImmediate = true;
              break;
            case 'multiple_faces':
              message = 'Multiple faces detected! Only you should be visible.';
              setFaceDetectionStatus('multiple_faces');
              isImmediate = true;
              severity = 'critical';
              break;
            case 'flat_surface':
              message = 'Camera appears to be covered. Please uncover it.';
              setFaceDetectionStatus('no_face');
              isImmediate = true;
              break;
            case 'too_dark':
              message = 'Video is too dark. Please improve lighting.';
              break;
            case 'too_bright':
              message = 'Video is too bright. Please adjust lighting.';
              break;
            case 'device_detected':
              message = 'Phone or device detected! Please remove it from view.';
              severity = 'critical';
              isImmediate = true;
              break;
            case 'eyes_not_visible':
              // Handled by continuous timer in startProctoring, skip here
              break;
            default:
              message = 'Proctoring issue detected. Please ensure you are visible.';
              isImmediate = true;
          }

          if (message && (isImmediate || !gracePeriodActive)) {
            issueWarning(message);
            await logProctoringEvent(warning, severity, {
              message,
              face_count: detectedFaces
            });
          }
        }
      } else if (detectedFaces === 1) {
        setFaceDetectionStatus('ok');
      }

      // Device detection (if not already in warnings)
      if (!gracePeriodActive && analysis.device_detected &&
          !(analysis.warnings || []).includes('device_detected')) {
        issueWarning('Phone or device detected! Please remove it from view.');
        await logProctoringEvent('device_detected', 'critical', {
          message: 'Device detected in frame',
          device_count: analysis.objects?.length || 1
        });
      }

    } catch (err) {
      console.error('Error analyzing frame:', err);
    }
  };

  const issueWarning = (message) => {
    const now = Date.now();
    const WARNING_COOLDOWN = 5000; // 5 seconds between warnings

    if (now - lastWarningTime < WARNING_COOLDOWN) return;

    setLastWarningTime(now);
    setWarningCount(prev => prev + 1);

    // Show warning banner
    setWarningBanner({ show: true, message });
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    warningTimeoutRef.current = setTimeout(() => {
      setWarningBanner({ show: false, message: '' });
    }, 5000);
  };

  const logProctoringEvent = async (eventType, severity, details) => {
    try {
      const response = await fetch(`${API_BASE_URL}/interview/log-proctoring-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_id: applicationId,
          event_type: eventType,
          severity,
          details
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.total_warnings > 0) {
          setWarningCount(data.total_warnings);
        }
        setWarnings(prev => [...prev, {
          type: eventType,
          message: details.message,
          timestamp: new Date().toISOString()
        }].slice(-10));
      }
    } catch (err) {
      console.error('Error logging proctoring event:', err);
    }
  };

  const saveInterviewData = async () => {
    try {
      // Check if this is a placement training interview
      const interviewType = location.state?.type;
      const isPlacementInterview = interviewType === 'placement_tech' || interviewType === 'placement_hr';
      
      if (isPlacementInterview) {
        // Save placement interview data
        const assessmentId = location.state?.assessmentId;
        const roundId = location.state?.roundId;
        
        if (!assessmentId || !roundId) {
          console.error('Missing assessmentId or roundId for placement interview');
          return { next_round: 'completed' };
        }
        
        // Get session from SessionManager
        const session = JSON.parse(localStorage.getItem('candidate_session'));
        if (!session) {
          console.error('No session found');
          return { next_round: 'completed' };
        }
        
        // Wait a bit for the WebSocket backend to finish evaluation
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Fetch the evaluation result from LiveInterviewSession
        let evaluationResult = null;
        let overallScore = 75; // Default fallback
        
        try {
          const evalResponse = await fetch(
            `${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/live-interview/session/${applicationId}/evaluation`,
            {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              }
            }
          );
          
          if (evalResponse.ok) {
            const evalData = await evalResponse.json();
            evaluationResult = evalData.evaluation_result;
            overallScore = evalData.overall_score || 75;
            console.log('✅ Retrieved evaluation from live interview session:', overallScore);
          } else {
            console.warn('Could not fetch evaluation, using default score');
          }
        } catch (evalError) {
          console.error('Error fetching evaluation:', evalError);
        }
        
        const placementData = {
          assessment_id: assessmentId,
          round_id: roundId,
          interview_session_id: applicationId,
          overall_score: overallScore,
          transcript: transcript.reduce((acc, item) => {
            acc[item.timestamp || new Date().toISOString()] = item.text;
            return acc;
          }, {}),
          evaluation_result: evaluationResult || {
            transcript: transcript,
            warnings: warnings,
            warning_count: warningCount,
            duration_minutes: interviewConfig.duration || location.state?.duration,
            completed_at: new Date().toISOString()
          }
        };
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/placement-training/interview/complete`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(placementData)
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to save placement interview data');
        }
        
        const result = await response.json();
        console.log('Placement interview data saved successfully', result);
        return result; // Return the response with next_round
      } else {
        // Save regular job application interview data
        const interviewData = {
          application_id: applicationId,
          transcript: transcript,
          warnings: warnings,
          warning_count: warningCount,
          duration_minutes: interviewConfig.duration,
          completed_at: new Date().toISOString()
        };
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/live-interview/save`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(interviewData)
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to save interview data');
        }
        
        console.log('Interview data saved successfully');
        return { next_round: 'completed' };
      }
      
    } catch (error) {
      console.error('Error saving interview data:', error);
      return { next_round: 'completed' };
    }
  };

  const cleanup = () => {
    stopTimer();
    stopProctoring();

    // Stop video recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach(track => track.stop());
    }

    if (faceDetectionRef.current) {
      clearInterval(faceDetectionRef.current);
    }

    if (cursorTrackingRef.current) {
      cursorTrackingRef.current();
    }

    // Stop audio recording
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      audioRecorderRef.current.stop();
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }

    if (rtviClientRef.current) {
      rtviClientRef.current.disconnect();
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (interviewEnded) {
    const isTrainerSession = interviewConfig.interviewType === 'trainer';
    return (
      <div className="relative flex h-screen w-full flex-col overflow-x-hidden">
        <Header variant="default" />
        <main className="layout-container flex h-full grow flex-col items-center justify-center">
          <div className="text-center">
            <Icon name="check_circle" size={64} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
              {isTrainerSession ? 'Training Complete!' : 'Interview Completed'}
            </h2>
            <p className="text-text-light-secondary dark:text-text-dark-secondary mb-6">
              {isTrainerSession
                ? 'Your training session is being evaluated. Redirecting to your reports...'
                : 'Thank you for completing the live interview. Your responses are being processed.'}
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="relative flex h-screen w-full flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800"
    >
      {/* Warning Banner - Fixed at top */}
      {warningBanner.show && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-red-800 text-white shadow-lg border-b border-red-900">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex items-center gap-4">
              <span className="text-xs font-semibold tracking-widest uppercase text-red-200">
                Proctoring Alert
              </span>
              <span className="text-sm font-medium">{warningBanner.message}</span>
            </div>
            <button
              onClick={() => setWarningBanner({ show: false, message: '' })}
              className="text-red-300 hover:text-white text-sm font-bold ml-4 px-2"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Cursor Out of Bounds Warning */}
      {cursorOutOfBounds && (
        <div className="absolute inset-0 border-4 border-red-500 pointer-events-none z-40">
          <div className="absolute top-12 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
            Keep cursor in interview area
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="record_voice_over" size={20} className="text-primary" />
            <span className="text-gray-900 dark:text-white font-semibold text-sm">Live Interview Session</span>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Timer */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg">
              <Icon name="schedule" size={18} className="text-yellow-500" />
              <span className={`font-mono text-sm font-semibold ${
                timeRemaining < 60 ? 'text-red-500' : 'text-gray-700 dark:text-gray-200'
              }`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            
            {/* Connection Status */}
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'Interview Complete' ? 'bg-blue-500' :
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span className={`text-xs font-medium ${
                connectionStatus === 'Interview Complete' ? 'text-blue-600 dark:text-blue-400' :
                isConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'
              }`}>{connectionStatus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        {/* LEFT SIDE - Camera & Monitoring */}
        <div className="w-2/5 flex flex-col gap-4">
          {/* Video Feed */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="relative aspect-video bg-gray-900">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              
              {/* Face Detection Overlay */}
              <div className="absolute top-3 right-3">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold backdrop-blur-sm ${
                  faceDetectionStatus === 'ok'
                    ? 'bg-green-500/90 text-white'
                    : faceDetectionStatus === 'multiple_faces'
                    ? 'bg-yellow-500/90 text-white'
                    : faceDetectionStatus === 'no_face'
                    ? 'bg-red-500/90 text-white'
                    : 'bg-gray-500/90 text-white'
                }`}>
                  <Icon
                    name={faceDetectionStatus === 'ok' ? "face" :
                          faceDetectionStatus === 'multiple_faces' ? "group" :
                          "face_retouching_off"}
                    size={14}
                  />
                  <span>
                    {faceDetectionStatus === 'ok' ? `${faceCount} Face` :
                     faceDetectionStatus === 'multiple_faces' ? `${faceCount} Faces!` :
                     faceDetectionStatus === 'no_face' ? 'No Face' :
                     'Checking...'}
                  </span>
                </div>
              </div>

              {/* Warning Count */}
              {warningCount > 0 && (
                <div className="absolute top-3 left-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500/90 text-white backdrop-blur-sm">
                    <Icon name="warning" size={14} />
                    <span>{warningCount} Warnings</span>
                  </div>
                </div>
              )}

              {/* Center Guide */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-40 h-52 border-2 border-dashed border-white/40 rounded-lg"></div>
              </div>
            </div>
          </div>

          {/* Monitoring Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="monitor_heart" size={18} className="text-primary" />
              Monitoring Status
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {/* Face Detection */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon
                  name="face"
                  size={24}
                  className={faceDetectionStatus === 'ok' ? 'text-green-500 mx-auto mb-1' : 'text-red-500 mx-auto mb-1'}
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Face</div>
                <div className={`text-xs font-bold ${faceDetectionStatus === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                  {faceDetectionStatus === 'ok' ? 'OK' :
                   faceDetectionStatus === 'multiple_faces' ? `${faceCount}!` :
                   faceDetectionStatus === 'no_face' ? 'Missing' : 'Checking'}
                </div>
              </div>

              {/* Eye Tracking */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon
                  name="visibility"
                  size={24}
                  className={eyesVisible ? 'text-green-500 mx-auto mb-1' : 'text-red-500 mx-auto mb-1'}
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Eyes</div>
                <div className={`text-xs font-bold ${eyesVisible ? 'text-green-600' : 'text-red-600'}`}>
                  {eyesVisible ? 'Visible' : 'Not Visible'}
                </div>
              </div>

              {/* Device Detection */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon
                  name="phone_android"
                  size={24}
                  className={!deviceDetected ? 'text-green-500 mx-auto mb-1' : 'text-red-500 mx-auto mb-1'}
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Device</div>
                <div className={`text-xs font-bold ${!deviceDetected ? 'text-green-600' : 'text-red-600'}`}>
                  {!deviceDetected ? 'Clear' : 'Detected!'}
                </div>
              </div>

              {/* Mic Status */}
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Icon
                  name={isMicEnabled ? "mic" : "mic_off"}
                  size={24}
                  className={isMicEnabled ? 'text-green-500 mx-auto mb-1' : 'text-gray-400 mx-auto mb-1'}
                />
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Mic</div>
                <div className={`text-xs font-bold ${isMicEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {isMicEnabled ? 'Active' : 'Muted'}
                </div>
              </div>
            </div>
          </div>

          {/* Training Info */}
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-xl shadow-lg p-4 border border-primary/20">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="school" size={18} className="text-primary" />
              Training Session
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Role:</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{interviewConfig.jobTitle}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Type:</span>
                <span className="text-sm font-semibold text-primary capitalize">{interviewConfig.interviewType}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">Duration:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{interviewConfig.duration} min</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - AI Chat & Controls */}
        <div className="flex-1 flex flex-col gap-4">
          {/* AI Status Header */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-md ${
                isSpeaking ? 'bg-blue-500' : 
                isListening ? 'bg-green-500' : 
                'bg-gray-400'
              }`}>
                <Icon 
                  name={isSpeaking ? "record_voice_over" : isListening ? "hearing" : "psychology"} 
                  size={24} 
                  className="text-white" 
                />
              </div>
              <div className="flex-1">
                <div className="text-base font-bold text-gray-900 dark:text-white">AI Interviewer</div>
                <div className={`text-sm font-medium ${
                  isSpeaking ? 'text-blue-600' : 
                  isListening ? 'text-green-600' : 
                  'text-gray-500'
                }`}>
                  {isSpeaking ? 'Speaking...' :
                   isListening ? 'Listening...' :
                   interviewStarted ? 'Waiting...' : 'Ready to start'}
                </div>
              </div>
            </div>
          </div>

          {/* Transcript Area */}
          <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Icon name="chat" size={18} className="text-primary" />
                Conversation
              </h3>
            </div>
            
            <div className="flex-1 p-4 overflow-y-auto">
              {transcript.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <Icon name="chat_bubble_outline" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                      {interviewStarted ? 'Conversation will appear here...' : 'Ready to begin your interview'}
                    </p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs">
                      {interviewStarted ? 'The AI will start speaking shortly' : 'Click "Start Interview" to begin'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {transcript.map((entry, index) => (
                    <div key={index} className={`flex ${entry.speaker === 'User' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-lg shadow-sm ${
                        entry.speaker === 'User' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                      }`}>
                        <div className="text-xs font-semibold mb-1 opacity-75">
                          {entry.speaker}
                        </div>
                        <div className="text-sm leading-relaxed">
                          {entry.text}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 border border-gray-200 dark:border-gray-700">
            {!interviewStarted ? (
              <Button
                size="lg"
                onClick={startInterview}
                disabled={false}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 text-base shadow-md flex items-center justify-center gap-2"
              >
                <Icon name="call" size={22} />
                Start Call
              </Button>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <Button
                  size="md"
                  onClick={toggleMicrophone}
                  className={`font-semibold flex items-center justify-center gap-1.5 ${
                    isMicEnabled 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Icon name={isMicEnabled ? "mic" : "mic_off"} size={18} />
                  {isMicEnabled ? 'Mute' : 'Unmute'}
                </Button>
                <Button
                  size="md"
                  onClick={() => {/* Skip question logic */}}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold flex items-center justify-center gap-1.5"
                  disabled={!interviewStarted || interviewEnded}
                >
                  <Icon name="skip_next" size={18} />
                  Skip
                </Button>
                <Button
                  size="md"
                  onClick={endInterview}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-1.5"
                >
                  <Icon name="call_end" size={18} />
                  End Call
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden Audio Element for Bot Voice */}
      <audio ref={audioRef} autoPlay />
    </div>
  );
};

export default LiveInterviewSessionPage;
